import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { CContainer } from '@coreui/react';
import Sidebar from './components/Layout/Sidebar';
import Header from './components/Layout/Header';
import Login from './components/Auth/Login';
import Register from './components/Auth/Register';
import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';
import Profile from './pages/Profile';
import SoftwareProducts from './pages/SoftwareProducts';
import Subscriptions from './pages/Subscriptions';
import ClientProducts from './pages/ClientProducts';
import ThankYouPage from "./pages/ThankYouPage";
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import SuperAdminResellers from './pages/SuperAdminResellers';
import SuperAdminClients from './pages/SuperAdminClients';
import SuperAdminOrders from './pages/SuperAdminOrders';
import SuperAdminSerialNoGeneration from './pages/SuperAdminSerialNoGeneration';
import '@coreui/coreui/dist/css/coreui.min.css';
import { api } from './services/api';

function App() {
  const [sidebarShow, setSidebarShow] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Check if token is expired
  const isTokenExpired = (token) => {
    if (!token) return true;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp < Date.now() / 1000;
    } catch (error) {
      return true;
    }
  };

  // Handle logout
  const handleLogout = (isTokenExpired = false) => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('userInfo');
    setIsAuthenticated(false);
    setUser(null);
    
    navigate('/login', { 
      state: { sessionExpired: isTokenExpired } 
    });
  };

  // Check authentication on app load and regularly
  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      const userInfo = localStorage.getItem('userInfo');

      if (token && userData && userInfo) {
        if (isTokenExpired(token)) {
          handleLogout(true);
        } else {
          setIsAuthenticated(true);
          setUser(JSON.parse(userData));
        }
      } else {
        setIsAuthenticated(false);
        setUser(null);
      }
      setLoading(false);
    };

    checkAuth();

    // Check every 5 seconds if token expired
    const interval = setInterval(checkAuth, 5000);

    return () => clearInterval(interval);
  }, []);

  // Handle login
  const handleLogin = (userData) => {
    setIsAuthenticated(true);
    setUser(userData);
    
    // Check if it's a super admin or regular user
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
    
    if (userInfo.is_super_admin) {
      // Redirect super admin to super admin dashboard
      navigate('/dashboard'); // Still go to /dashboard but show SuperAdminDashboard
    } else {
      // Regular user goes to regular dashboard
      navigate('/dashboard');
    }
  };

  const handleRegisterSuccess = () => {
    navigate('/login');
  };

  // API interceptor for 401 errors
  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          handleLogout(true);
        }
        return Promise.reject(error);
      }
    );

    return () => api.interceptors.response.eject(interceptor);
  }, []);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login onLogin={handleLogin} />} />
        <Route path="/register" element={<Register onRegisterSuccess={handleRegisterSuccess} />} />
        <Route path="*" element={<Navigate to="/login" />} />
      </Routes>
    );
  }

  // Check if current user is super admin
  const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
  const isSuperAdmin = userInfo.is_super_admin;

  return (
    <>
      <div className="header-fixed">
        <Header 
          sidebarShow={sidebarShow} 
          setSidebarShow={() => setSidebarShow(!sidebarShow)}
          onLogout={() => handleLogout(false)}
          user={user}
        />
      </div>
      
      <div className={`sidebar-fixed ${sidebarShow ? 'visible' : ''}`}>
        <Sidebar 
          sidebarShow={sidebarShow} 
          setSidebarShow={setSidebarShow} 
        />
      </div>
      
      <div className="main-content-scrollable">
        <CContainer fluid className="p-3">
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" />} />
            <Route path="/dashboard" element={
              isSuperAdmin ? <SuperAdminDashboard /> : <Dashboard />
            } />
            <Route path="/clients" element={<Clients />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/software-products" element={<SoftwareProducts />} />
            <Route path="/subscriptions" element={<Subscriptions />} />
            <Route path="/client/:clientId/products" element={<ClientProducts />} />
            <Route path="/ThankYouPage" element={<ThankYouPage />} />
            <Route path="/super-admin/resellers" element={<SuperAdminResellers />} />
            <Route path="/super-admin/clients" element={<SuperAdminClients />} />
            <Route path="/super-admin/orders" element={<SuperAdminOrders />} />
            <Route path="/super-admin/serial-number-generation" element={<SuperAdminSerialNoGeneration />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </CContainer>
      </div>
    </>
  );
}

export default App;